/**
 * Which staging kits a room gets, in planning order — empty when no stager
 * claims it. Every gate asks this one question (`stagingKitsFor(room).length`):
 * the `style_room_decor` tool, the Add accessories menu, the stage-room
 * picker and the re-plan of a staged room. A new kit is one entry in
 * `KIT_CLAIMS` and one planner in `planStagingObjects`, not a gate edit in five files.
 *
 * An open plan is dressed AREA by area, each by what it is (Megan,
 * 2026-10-29): the kitchen kit on the counters, the bedroom kit at the bed,
 * the bathroom kit at the fixtures — all in one room. A later kit plans around
 * an earlier one's props.
 *
 * - kitchen: decided the way the kitchen layout generator decides it
 *   (`isKitchenRoom`, kitchen-assembly/layoutKitchen.js): a DECLARED kitchen
 *   kind, not a guess from the room's name, and not a bathroom the scan
 *   mislabelled (`isSpuriousKitchen`). Its two primitives are imported directly
 *   rather than `isKitchenRoom` itself, which would pull the generator's
 *   cabinet builders into the web bundle; a test keeps the two in step. A
 *   living/dining/kitchen room gets the kitchen kit on its counters (Megan,
 *   2026-10-18).
 * - bedroom: a bedroom kind with a bed the kit can dress (`pickStagedBed` —
 *   any `bed`, a tall one included; never a dorm bed). Before the bathroom kit: a room that
 *   is both gets the bed's zone first (Megan, 2026-10-28).
 * - bathroom: `resolveRoomKind`, which includes the name fallback, so the set
 *   of rooms that get bathroom staging is exactly what it was before kitchens.
 */

const {
  resolveRoomKind,
  resolveDeclaredRoomKind,
} = require('../rooms/roomKind');
const { isSpuriousKitchen } = require('../room-fixtures');
const { pickStagedBed } = require('./plan/bedroom/bedHost');

// Each kit and the rooms it claims, in PLANNING order.
const KIT_CLAIMS = {
  kitchen: (room) =>
    resolveDeclaredRoomKind(room).has('kitchen') && !isSpuriousKitchen(room),
  bedroom: (room) =>
    resolveRoomKind(room).has('bedroom') && !!pickStagedBed(room).bed,
  bathroom: (room) => resolveRoomKind(room).has('bathroom'),
};

/** Every staging kit, in planning order. */
const STAGING_KITS = Object.freeze(Object.keys(KIT_CLAIMS));

/** Does `room` take `kit`? */
const roomTakesKit = (room, kit) => !!room && !!KIT_CLAIMS[kit]?.(room);

/**
 * @param {Object|null} room
 * @returns {('kitchen'|'bedroom'|'bathroom')[]}
 */
function stagingKitsFor(room) {
  return STAGING_KITS.filter((kit) => roomTakesKit(room, kit));
}

module.exports = { STAGING_KITS, stagingKitsFor, roomTakesKit };
